import { gettext } from '@adjust/dashboard-core/lib/utils/localization';
import { AboutPermissionsSidebar } from '@react/Pages/UsersPage/AboutPermissions/AboutPermissionsSidebar';
import { SsoConfigurationSidebar } from '@react/Pages/AccountSettingsPage/YourDataSection/SsoConfigurationSidebar/SsoConfigurationSidebar';
import { CreateUserPopup } from '@react/Pages/UsersPage/CreateUserPopup/CreateUserPopup';
import { AccountDetailsSidebar } from '@react/Pages/AccountSettingsPage/YourDataSection/AccountDetailsSidebar/AccountDetailsSidebar';
import { UserDetailsSidebar } from '@react/Pages/AccountSettingsPage/UserDetails/UserDetails/UserDetailsSidebar';
import { PaymentMethodSidebar } from '@react/Pages/AccountSettingsPage/YourDataSection/PaymentMethodSidebar/PaymentMethodSidebar';
import { TwoFactorConfigurationSidebar } from '@react/Pages/AccountSettingsPage/YourDataSection/TwoFactorConfigurationSidebar/TwoFactorConfigurationSidebar';
import { UserPermissionsSidebar } from '@react/Pages/UsersPage/UserPermissionSidebar/Sidebar/Sidebar.container';
import { CreateUserSidebar } from '@react/Pages/UsersPage/CreateUserSidebar/CreateUserSidebar.container';

const loadModule = $ocLazyLoad =>
  import('./account-settings.js').then(module => {
    $ocLazyLoad.load({
      name: module.default,
    });
  });

export default function config($stateProvider, conf, $urlRouterProvider) {
  'ngInject';
  $stateProvider
    .state('accountSettings', {
      url: '/',
      template: () => import('./account-settings.html').then(res => res.default),
      controller: 'AccountSettingsController',
      controllerAs: 'vm',
      resolve: {
        loadModule,
        $transition$: '$transition$',
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, $transition$) {
          return RouteResolver.resolve('accountSettings', null, {target: $transition$.to().name});
        },
      },
      data: {
        title: gettext('title.account-settings'),
      },
    })
    .state('accountSettings.account-data', {
      url: 'data',
      template: () => import('./account-data/account-data.html').then(res => res.default),
      controller: 'AccountDataController',
      controllerAs: 'vm',
      resolve: {
        loadModule,
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.account-data', resolved);
        },
      },
      data: {
        title: gettext('title.your-data'),
      },
    })
    .state('accountSettings.users', {
      url: 'users',
      template: () => import('./users/users.html').then(res => res.default),
      controller: 'UsersController',
      controllerAs: 'vm',
      resolve: {
        loadModule,
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.users', resolved);
        },
      },
      data: {
        title: gettext('title.users'),
      },
    })
    .state('accountSettings.users.about-permissions', {
      url: '/about-permissions',
      component: AboutPermissionsSidebar,
      resolve: {
        routerProps: () => ({
          backPath: 'accountSettings.users',
        }),
      },
    })
    .state('accountSettings.users.role-selection', {
      url: '/role-selection',
      component: CreateUserPopup,
      resolve: {
        routerProps: () => ({
          backPath: 'accountSettings.users',
          defaultPath: 'accountSettings.users.create-user',
          advancedPermissionsPath: conf.advancedPermissions.baseUrl,
        }),
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.users.role-selection', resolved);
        },
      },
    })
    .state('accountSettings.users.create-user', {
      url: '/create-user',
      component: CreateUserSidebar,
      resolve: {
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.users.create-user', resolved);
        },
        routerProps: () => ({
          backPath: 'accountSettings.users',
        }),
      },
    })
    .state('accountSettings.users.details', {
      url: '/details/:id',
      component: UserDetailsSidebar,
      resolve: {
        $transition$: '$transition$',
        RouteResolver: 'RouteResolver',
        resolved: function (RouteResolver, resolved, $transition$) {
          return RouteResolver.resolve('accountSettings.users.details', resolved, $transition$.params());
        },
        routerProps: () => ({
          backPath: 'accountSettings.users',
        }),
      },
    })
    .state('accountSettings.users.access', {
      url: '/access/:id',
      component: UserPermissionsSidebar,
      resolve: {
        $transition$: '$transition$',
        RouteResolver: 'RouteResolver',
        resolved: function (RouteResolver, resolved, $transition$) {
          return RouteResolver.resolve('accountSettings.users.access', resolved, $transition$.params());
        },
        routerProps: () => ({
          backPath: 'accountSettings.users',
        }),
      },
    })
    .state('accountSettings.account-data.company-details', {
      url: '/company-details',
      component: AccountDetailsSidebar,
      resolve: {
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.account-data.company-details', resolved);
        },
        routerProps: () => ({
          backPath: 'accountSettings.account-data',
        }),
      },
    })
    .state('accountSettings.account-data.payment-method', {
      url: '/payment-method',
      component: PaymentMethodSidebar,
      resolve: {
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.account-data.payment-method', resolved);
        },
        routerProps: () => ({
          backPath: 'accountSettings.account-data',
        }),
      },
    })
    .state('accountSettings.account-data.user-details', {
      url: '/user-details',
      component: UserDetailsSidebar,
      resolve: {
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.account-data.user-details', resolved);
        },
        routerProps: () => ({
          backPath: 'accountSettings.account-data',
        }),
      },
    })
    .state('accountSettings.account-data.sso-configuration', {
      url: '/sso-configuration',
      component: SsoConfigurationSidebar,
      resolve: {
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.account-data.sso-configuration', resolved);
        },
        routerProps: () => ({
          backPath: 'accountSettings.account-data',
        }),
      },
    })
    .state('accountSettings.account-data.two-factor-authentication', {
      url: '/two-factor-authentication',
      component: TwoFactorConfigurationSidebar,
      resolve: {
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.account-data.two-factor-authentication', resolved);
        },
        routerProps: () => ({
          backPath: 'accountSettings.account-data',
        }),
      },
    })
    .state('accountSettings.invoices', {
      url: 'invoices',
      template: () => import('./invoices/invoices.html').then(res => res.default),
      controller: 'InvoicesController',
      controllerAs: 'vm',
      resolve: {
        loadModule,
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.invoices', resolved);
        },
      },
      data: {
        title: gettext('title.invoices'),
      },
    })
    .state('accountSettings.account-history', {
      url: 'history',
      template: () => import('./account-history/account-history.html').then(res => res.default),
      controller: 'AccountHistoryController',
      controllerAs: 'vm',
      resolve: {
        loadModule,
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.account-history', resolved);
        },
      },
      data: {
        title: gettext('title.account-history'),
      },
    })
    .state('accountSettings.addons-packages', {
      url: 'pricing-plan',
      template: () => import('./addons-packages/addons-packages.html').then(res => res.default),
      controller: 'AccountAddonsPackagesController',
      controllerAs: 'vm',
      resolve: {
        loadModule,
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.addons-packages', resolved);
        },
      },
      data: {
        title: gettext('title.addons-packages'),
      },
    })
    .state('accountSettings.payment-setup', {
      url: 'payment-setup',
      template: () => import('./payment-setup/payment-setup.html').then(res => res.default),
      controller: 'PaymentSetupController',
      controllerAs: 'vm',
      resolve: {
        loadModule,
        RouteResolver: 'RouteResolver',
        resolved: function(RouteResolver, resolved) {
          return RouteResolver.resolve('accountSettings.payment-setup', resolved);
        },
      },
      data: {
        title: gettext('title.payment-setup'),
      },
    });

    $urlRouterProvider.otherwise('/');
}
